import { FlexRow } from '@/components/common/Flex';
import { Paragraph, Text } from '@/components/common/Typography';
import { ITEMS_PER_PAGE } from '@/components/goals/GoalActivitiesSection';
import { toDollars, toDollarsWithCents } from '@/util/currency';
import { useMutation } from '@apollo/client';
import { FlexProps, Spinner } from '@chakra-ui/react';
import { assert } from '@common/errors';
import {
    AllFinancialEntitiesDocument,
    CreateTransactionDocument,
    CreateTransactionScheduleDocument,
    GoalActivitiesDocument,
    UpdateTransactionScheduleDocument,
} from '@common/graphql/generated';
import {
    getExpectedArrivalStr,
    GoalDirection,
} from '@common/react/transactions';
import { TOASTS } from '@common/toasts';
import { getRecurrenceConfig, Recurrence } from '@common/types';
import { getFirstBusinessDay } from '@common/util/businessDays';
import { dt } from '@common/util/dateTime';
import { isNumber } from '@common/util/types';
import { useState } from 'react';
import { InfoCircle } from '../common/icons';
import { InfoButtonWithModal } from '../common/InfoModal';
import KeyValueGrid from '../common/KeyValueGrid';
import Notice from '../common/Notice';
import { useToasts } from '../common/Toasts';
import TransactionCardLayout from './TransactionCardLayout';
import TransactionSourceOrDestinationLockup from './TransactionSourceOrDestinationLockup';

export interface TransactionConfirmationCardProps extends FlexProps {
    title: string;
    nextButtonText: string;
    sourceId: string;
    destinationId: string;
    amount: number;
    fullWithdrawalDescription?: string;
    contributionLimitStr?: string;
    goalDirection: GoalDirection;
    goalId: string;
    pendingWithdrawalAmount: number | null;
    date: string;
    recurrence?: Recurrence;
    existingTransactionScheduleId?: string;
    onBack: VoidFunction;
    onCompleted: VoidFunction;
    onClose: VoidFunction;
}

const TransactionConfirmationCard = ({
    title,
    nextButtonText,
    sourceId,
    destinationId,
    amount,
    fullWithdrawalDescription,
    contributionLimitStr,
    goalDirection,
    goalId,
    pendingWithdrawalAmount,
    date,
    recurrence,
    existingTransactionScheduleId,
    onBack,
    onCompleted,
    onClose,
    ...props
}: TransactionConfirmationCardProps) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showErrorToast } = useToasts();
    const refetchQueries = [
        { query: AllFinancialEntitiesDocument },
        {
            query: GoalActivitiesDocument,
            variables: {
                goalId,
                financialEntityFilter: [
                    { financialEntityId: { equalTo: destinationId } },
                    { financialEntityId: { equalTo: sourceId } },
                    { financialEntityId: { isNull: true } },
                ],
                itemsPerPage: ITEMS_PER_PAGE,
            },
        },
    ];
    const [createTransaction] = useMutation(CreateTransactionDocument, {
        refetchQueries,
    });
    const [createTransactionSchedule] = useMutation(
        CreateTransactionScheduleDocument,
        { refetchQueries }
    );
    const [updateTransactionSchedule] = useMutation(
        UpdateTransactionScheduleDocument,
        { refetchQueries }
    );
    const amountStr = toDollars(amount, { withCents: true });
    const arrivalStr = getExpectedArrivalStr(dt(date), goalDirection);
    const plannedProcessingDate = getFirstBusinessDay(dt(date)).toDateStr();
    const startStr = dt(plannedProcessingDate).format('MMM D, YYYY');

    if (!isNumber(pendingWithdrawalAmount)) {
        return <Spinner size="sm" color="gray.500" />;
    }

    const submit = () => {
        if (isSubmitting || !amount || !sourceId || !destinationId) return;
        setIsSubmitting(true);

        if (existingTransactionScheduleId) {
            // Existing transaction schedule
            assert(
                recurrence,
                'Recurrence is required to update existing transaction schedule'
            );

            updateTransactionSchedule({
                variables: {
                    transactionScheduleId: existingTransactionScheduleId,
                    amount,
                    startDate: date,
                    recurrence,
                    nextTransactionDate: plannedProcessingDate,
                    sourceId,
                },
                onCompleted: () => {
                    setIsSubmitting(false);
                    onCompleted();
                },
                onError: () => {
                    setIsSubmitting(false);
                    showErrorToast(TOASTS.updateTransactionScheduleError);
                },
            });
        } else if (recurrence) {
            // New transaction schedule

            createTransactionSchedule({
                variables: {
                    sourceId,
                    destinationId,
                    amount,
                    startDate: date,
                    recurrence,
                    nextTransactionDate: plannedProcessingDate,
                    goalId,
                },
                onCompleted: () => {
                    setIsSubmitting(false);
                    onCompleted();
                },
                onError: () => {
                    setIsSubmitting(false);
                    showErrorToast(TOASTS.createTransactionScheduleError);
                },
            });
        } else {
            // New one-time transaction
            createTransaction({
                variables: {
                    sourceId,
                    destinationId,
                    amount,
                    plannedProcessingDate,
                    isFullWithdrawal: !!fullWithdrawalDescription,
                },
                onCompleted: () => {
                    setIsSubmitting(false);
                    onCompleted();
                },
                onError: () => {
                    setIsSubmitting(false);
                    showErrorToast(TOASTS.createTransactionError);
                },
            });
        }
    };

    const items = [
        {
            key: 'Transfer amount',
            value: (
                <>
                    <Text>{amountStr} </Text>
                    {fullWithdrawalDescription ? (
                        <InfoButtonWithModal
                            title="Withdrawing total balance"
                            buttonProps={{
                                onClickEventParams: {
                                    location: 'Transaction Confirmation Card',
                                },
                            }}
                        >
                            {fullWithdrawalDescription}
                        </InfoButtonWithModal>
                    ) : null}
                </>
            ),
        },
        {
            key: 'Transfer from',
            value: (
                <TransactionSourceOrDestinationLockup
                    financialEntityId={sourceId}
                />
            ),
        },
        {
            key: 'Transfer to',
            value: (
                <TransactionSourceOrDestinationLockup
                    financialEntityId={destinationId}
                />
            ),
        },
        {
            key: 'Frequency',
            value: recurrence
                ? `Every ${getRecurrenceConfig(recurrence).freqString}`
                : 'One time',
        },
        {
            key: recurrence ? 'Starting on' : 'Processing on',
            value: startStr,
        },
        { key: 'Expected arrival', value: arrivalStr },
    ];

    return (
        <TransactionCardLayout
            title={title}
            onBack={isSubmitting ? undefined : onBack}
            nextButtonText={nextButtonText}
            isNextLoading={isSubmitting}
            onNext={submit}
            onClose={onClose}
            {...props}
        >
            <KeyValueGrid items={items} mt={2} />
            {contributionLimitStr && (
                <Notice mt={5}>{contributionLimitStr}</Notice>
            )}
            {pendingWithdrawalAmount > 0 ? (
                <FlexRow mt={5}>
                    <InfoCircle mt={1} mr={3} />
                    <Paragraph fontSize="sm" color="textMuted">
                        Heads up - there are{' '}
                        <Text
                            color="textDark"
                            fontSize="sm"
                            fontWeight="semibold"
                        >
                            {toDollarsWithCents(pendingWithdrawalAmount)}
                        </Text>{' '}
                        of transfers that are in progress from this account.
                    </Paragraph>
                </FlexRow>
            ) : null}
        </TransactionCardLayout>
    );
};

export default TransactionConfirmationCard;
